import { X } from "lucide-react";
import { useToastList, type ToastKind } from "./ToastContext";

// Toaster renders the live toast list top-center, fixed, above the page so
// transient feedback is seen regardless of scroll position (bottom-right hid
// errors behind page chrome). Mounted once in
// main.tsx (a single host for the whole app). Reads state via useToastList;
// emitting happens through useToast elsewhere.
//
// Colors are gruvbox tokens only (manifest §10: no literal colors), held as a
// map of FULL static class strings — Tailwind v4's JIT scanner only emits
// classes it can see as complete literals, so `text-${color}` interpolation
// would silently drop the color. The kind maps to one semantic color —
// error=red, success=green, info=aqua — matching CLAUDE.md's UI color policy.
const kindClassMap: Record<ToastKind, { text: string; border: string }> = {
  error: { text: "text-gruvbox-red", border: "border-gruvbox-red/40" },
  success: { text: "text-gruvbox-green", border: "border-gruvbox-green/40" },
  info: { text: "text-gruvbox-aqua", border: "border-gruvbox-aqua/40" },
};

export function Toaster() {
  const { toasts, dismiss } = useToastList();

  if (toasts.length === 0) return null;

  return (
    // Wrapper is pointer-events-none so it never blocks clicks on the page
    // behind it; each toast re-enables pointer events for its own close button.
    <div className="pointer-events-none fixed inset-x-0 top-0 z-50 mx-auto flex max-w-[24rem] flex-col items-center gap-sm p-lg">
      {toasts.map((t) => {
        const cls = kindClassMap[t.kind];
        return (
          <div
            key={t.id}
            role="alert"
            className={`pointer-events-auto flex items-start gap-sm rounded-lg border bg-gruvbox-bg1 p-md text-sm shadow-lg ${cls.border}`}
          >
            <span className={`min-w-0 flex-1 break-words ${cls.text}`}>{t.message}</span>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
              className="shrink-0 text-gruvbox-gray transition-colors hover:text-gruvbox-fg"
            >
              <X size={16} strokeWidth={1.5} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
